import { WintSuperkollAPIModel as ApiModels, WintUtilsWebApi } from "wint-api-client/dist/types";
import { SelectionItem } from "models/utilTypes/selectionTypes";
import { LocalizationHelperInstance } from "utils/helpers";
import { TranslationKeys } from "translations";
import { AutocompleteListItem } from "models/propTypes/listPropTypes";
const UtilsHelper = {
  convertToResultItem: (result: Promise<WintUtilsWebApi.SearchResult[]>): Promise<SelectionItem[]> => {
    return result.then((res) => {
      return res.map((item) => {
        return {
          id: item.id,
          value: item.name,
          avatar: {
            id: item.id as unknown as number,
            name: item.name,
          },
        } as SelectionItem;
      });
    });
  },

  convertToResultAutoCompleteItem: (
    result: Promise<WintUtilsWebApi.SearchResult[]>
  ): Promise<AutocompleteListItem[]> => {
    return result.then((res) => {
      return res.map((item): AutocompleteListItem => {
        return {
          id: item.id,
          value: item.name ?? "",
        };
      });
    });
  },

  undefinedOrNull: (value: any): boolean => {
    return value === undefined || value === null;
  },

  notUndefinedOrNull: (value: any): boolean => {
    return value !== undefined && value !== null;
  },

  isEmpty: (value: any): boolean => {
    return value === undefined || value === null || value === "";
  },

  capitalizeFirst: (text: string) => {
    return text.charAt(0).toUpperCase() + text.slice(1);
  },

  splitMultipleEmails: (emailString?: string) => {
    return (
      emailString
        ?.split(",")
        .map((email) => email.trim())
        .filter((email) => !UtilsHelper.isEmpty(email)) ?? []
    );
  },

  getUniqueId: (): number => {
    return new Date().valueOf() + Math.random();
  },

  isClientGeneratedId: (id: number): boolean => {
    return id - Math.floor(id) !== 0;
  },

  applyMask: (value: string, mask: string) => {
    const maskLocations = mask.split("").filter((m) => m === "#").length;
    if (maskLocations > value.length) {
      let l = 0;
      mask = mask
        .split("")
        .map((m) => {
          if (m === "#") l++;
          return l <= value.length ? m : "";
        })
        .join("");
    }

    let i = 0;
    const v = value.toString();
    return mask.replace(/#/g, (_) => v[i++] ?? "");
  },

  getCountText: (count?: number): string => {
    if (count !== undefined)
      return `${count} ${LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.Pc)}`;
    else return "";
  },

  splitString: (text: string, maxLength: number): string => {
    const arr = text?.split(" ");
    const result = [];
    let subStr = arr[0];
    for (let i = 1; i < arr.length; i++) {
      const word = arr[i];
      if (subStr.length + word.length + 1 <= maxLength) {
        subStr = subStr + " " + word;
      } else {
        result.push(subStr);
        subStr = word;
      }
    }
    if (subStr.length) {
      result.push(subStr);
    }
    return result.length ? result[0] : "";
  },

  sleep: (ms: number) => {
    return new Promise((resolve) => setTimeout(resolve, ms));
  },

  isValueEquals: (value1: any, value2: any) => {
    switch (typeof value2) {
      case "boolean":
        return value1 === value2;
      case "number":
        // eslint-disable-next-line eqeqeq
        return value1 == value2;
      case "undefined":
        return value1 === undefined && value2 === undefined;
      case "string":
        if (UtilsHelper.isEmpty(value2)) {
          // eslint-disable-next-line eqeqeq
          return UtilsHelper.isEmpty(value2) == UtilsHelper.isEmpty(value1);
        } else {
          return value1 === value2;
        }
      case "object":
        if (UtilsHelper.isEmpty(value1)) {
          return false;
        } else {
          return UtilsHelper.compareObjects(value2, value1);
        }
      default:
        return false;
    }
  },

  compareObjects: (obj1: any, obj2: any): boolean =>
    Object.keys(obj1).length === Object.keys(obj2).length &&
    Object.keys(obj1).every((key) => obj2.hasOwnProperty(key) && UtilsHelper.isValueEquals(obj1[key], obj2[key])),

  areStringArraysEqual: (arr1: string[], arr2: string[]) => {
    return arr1.length === arr2.length && arr1.every((value) => arr2.includes(value));
  },

  hasDecimals: (num: number) => {
    return !!(num % 1);
  },

  truncateDecimals: (num: number, maxDecimalPlaces: number) => {
    return UtilsHelper.hasDecimals(num) ? num.toFixed(maxDecimalPlaces) : num.toString();
  },

  getSelectionNoneText: () => LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.None),

  isPlainObject: (value: any) => Object.prototype.toString.call(value) === "[object Object]",
  emptyFunc: () => {},

  groupBy: <T>(array: T[], key: string) => {
    const groupByKey = array.reduce((group, item) => {
      const keyValue = (item as any)[key];
      (group as any)[keyValue] = (group as any)[keyValue] ?? [];
      (group as any)[keyValue].push(item);
      return group;
    }, {});

    return Object.entries(groupByKey).map(([key, value]) => ({ key: key, value: value as T[] }));
  },

  getFormattedCompanyAddress: (companyAddress?: ApiModels.CompanyAdress) => {
    if (!companyAddress) return "";
    return `${companyAddress.street1}\n${companyAddress.zipCode} ${companyAddress.city}`;
  },

  // Promise.allSettled(items.map(worker)) starts every worker call at once, so for workers that each
  // hold a large payload in memory (a multi-megabyte file, say) a big enough batch can exhaust the
  // browser before any of them finish. This runs at most `limit` at a time, still resolving every
  // item regardless of whether others failed.
  runWithConcurrencyLimit: async <T, R>(
    items: T[],
    limit: number,
    worker: (item: T) => Promise<R>
  ): Promise<PromiseSettledResult<R>[]> => {
    const results: PromiseSettledResult<R>[] = new Array(items.length);
    let nextIndex = 0;

    const runNext = async (): Promise<void> => {
      const index = nextIndex++;
      if (index >= items.length) return;

      try {
        const value = await worker(items[index]);
        results[index] = { status: "fulfilled", value };
      } catch (reason) {
        results[index] = { status: "rejected", reason };
      }

      return runNext();
    };

    await Promise.all(Array.from({ length: Math.min(limit, items.length) }, runNext));
    return results;
  },

  getMultiHtmlPageCount: (iframeDoc?: Document) => {
    let pageCount = 1;

    if (iframeDoc) {
      const allPagesElem = iframeDoc.getElementsByClassName("all-pages");
      const pageElem = iframeDoc.getElementsByClassName("page");
      const multiPageElem = iframeDoc.getElementById("multiple-page-container");

      if (allPagesElem && allPagesElem.length > 0) {
        pageCount = pageElem.length - 1;
        if (multiPageElem && multiPageElem.clientHeight === 0) pageCount = pageCount - 1;
      }
    }
    return pageCount;
  },
};

export default UtilsHelper;
